import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, CheckCircle } from 'lucide-react';
import { suggestCategory } from '../utils/openai';

interface AICategorySuggestionProps {
  description: string;
  onCategorySuggested: (category: string) => void;
  disabled?: boolean;
}

export const AICategorySuggestion: React.FC<AICategorySuggestionProps> = ({
  description,
  onCategorySuggested,
  disabled = false,
}) => {
  const [suggestedCategory, setSuggestedCategory] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleSuggestCategory = useCallback(async () => {
    if (!description.trim() || disabled) return;

    setLoading(true);
    setError('');

    try {
      const category = await suggestCategory(description);
      setSuggestedCategory(category);
      onCategorySuggested(category);
    } catch (err) {
      setError('Failed to get AI suggestion');
      console.error('AI suggestion error:', err);
    } finally {
      setLoading(false);
    }
  }, [description, disabled, onCategorySuggested]);

  useEffect(() => {
    if (description.trim() && !disabled) {
      const timeoutId = setTimeout(() => {
        handleSuggestCategory();
      }, 1000); // Debounce for 1 second

      return () => clearTimeout(timeoutId);
    }
  }, [description, disabled, handleSuggestCategory]);

  const handleManualSuggest = () => {
    if (!description.trim()) return;
    handleSuggestCategory();
  };

  if (!description.trim()) return null;

  return (
    <div className="mt-2">
      {loading && (
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-primary-600"></div>
          <span>AI is suggesting a category...</span>
        </div>
      )}

      {suggestedCategory && !loading && (
        <div className="flex items-center space-x-2 text-sm">
          <CheckCircle className="w-4 h-4 text-success-600" />
          <span className="text-gray-600">AI suggests:</span>
          <button
            onClick={() => onCategorySuggested(suggestedCategory)}
            className="text-primary-600 hover:text-primary-700 font-medium underline"
          >
            {suggestedCategory}
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <span>{error}</span>
          <button
            onClick={handleManualSuggest}
            className="text-primary-600 hover:text-primary-700 underline"
          >
            Try again
          </button>
        </div>
      )}

      {!loading && !suggestedCategory && !error && description.trim() && (
        <button
          onClick={handleManualSuggest}
          disabled={disabled}
          className="flex items-center space-x-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
        >
          <Sparkles className="w-3 h-3" />
          <span>Get AI category suggestion</span>
        </button>
      )}
    </div>
  );
}; 